import { Injectable } from '@angular/core';
import { HttpResponse, HttpRequest } from '@angular/common/http';

import {
    ApplicationInsights,
    DistributedTracingModes,
    SeverityLevel,
    ITelemetryItem,
    Util
} from '@microsoft/applicationinsights-web';

import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';

import { Router } from '@angular/router';

import { environment } from 'src/environments/environment';
import { TenantContextService } from '../tenant-context/tenant-context.service';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';
import { removePageViewNameIds } from './app-insights.utils';

@Injectable({
    providedIn: 'root'
})
export class AppInsightsService {
    private appInsightsConnectionString: string;
    private appInsights: ApplicationInsights;
    private debugLogToConsole: boolean;
    private readonly cloudRoleName = 'config-ui';

    private previousParentId = '';

    public traceId: string;

    constructor(
        private router: Router,
        private tenantContext: TenantContextService,
        private oktaAuthService: OktaAuthService
    ) {
        this.appInsightsConnectionString = environment.appInsightsConnectionString;
        this.debugLogToConsole = environment.appInsightsServiceDebugLogToConsole;

        const angularPlugin = new AngularPlugin();
        let pageName = '';

        // https://learn.microsoft.com/en-us/azure/azure-monitor/app/javascript-feature-extensions?tabs=javascriptwebsdkloaderscript
        const clickPluginInstance = new ClickAnalyticsPlugin();
        const clickPluginConfig = {
            autoCapture: true,
            callback: {
                pageName: () => {
                    return pageName;
                }
            },

            dataTags: {
                useDefaultContentNameOrId: true
            }
        };

        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: this.appInsightsConnectionString,

                distributedTracingMode: DistributedTracingModes.W3C,
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableAutoRouteTracking: true,
                autoTrackPageVisitTime: true,
                enableAjaxErrorStatusText: true,
                overridePageViewDuration: true,
                // A same-origin window.open copies sessionStorage, so the new tab would re-send the opener's unsent buffered events.
                enableSessionStorageBuffer: false,

                extensions: [angularPlugin, <any>clickPluginInstance],
                extensionConfig: {
                    [angularPlugin.identifier]: { router: this.router },
                    [clickPluginInstance.identifier]: clickPluginConfig
                }
            }
        });

        this.appInsights.loadAppInsights();

        // Add a telemetry trace initializer, which runs before sending telemetry from the browser.
        // If the envelope's name is 'Microsoft.ApplicationInsights.{0}.RemoteDependency'

        // https://github.com/microsoft/ApplicationInsights-JS#telemetry-initializers
        // https://sgryphon.gamertheory.net/2021/02/20/app-insights-trace-correlation/
        // https://github.com/sgryphon/application-insights-react-demo/blob/master/client/src/TelemetryService.js
        const telemetryTraceContextInitializer = (envelope: ITelemetryItem) => {
            const tags = (envelope.tags ?? {}) as { [key: string]: any };
            tags['ai.cloud.role'] = `${this.cloudRoleName}-${environment.environmentCode.toLowerCase()}`;

            envelope.tags = tags as any;

            const telemetryTraceContext = this.appInsights.context?.telemetryTrace;

            if (telemetryTraceContext !== undefined) {
                pageName = removePageViewNameIds(envelope.ext?.['trace']['name']);
                // Add trace properties in the same format used by Microsoft.Extensions.Logging
                if (envelope.name === 'Microsoft.ApplicationInsights.{0}.RemoteDependency') {
                    envelope.baseData = envelope.baseData ?? {};
                    envelope.baseData['properties'] = envelope.baseData['properties'] ?? {};
                    envelope.baseData['properties'].TraceId = telemetryTraceContext.traceID ?? '';
                    // For AppDependency, parentID is the parent and the span needs to be parsed from ID
                    envelope.baseData['properties'].SpanId = envelope.baseData['id']?.slice(-17, -1) ?? '';
                    envelope.baseData['properties'].ParentId = telemetryTraceContext.parentID ?? '';

                    if (this.debugLogToConsole) {
                        console.log('remote dependency');
                    }
                } else {
                    envelope.baseData = envelope.baseData ?? {};
                    envelope.baseData['properties'] = envelope.baseData['properties'] ?? {};
                    envelope.baseData['properties'].TraceId = telemetryTraceContext.traceID ?? '';
                    // AppInsights treats traces as children of the current span, so stores current span-id in the parentID field.
                    // The createNewSpan() function then stores parent-id in the previousParentID field.
                    envelope.baseData['properties'].SpanId = telemetryTraceContext.parentID ?? '';
                    envelope.baseData['properties'].ParentId = this.previousParentId;
                    if (envelope.name === 'Microsoft.ApplicationInsights.{0}.Pageview') {
                        envelope.baseData['name'] = pageName;
                    }

                    if (this.debugLogToConsole) {
                        console.log('Non remote dependency');
                    }
                }

                // TODO: revisit this to see if there is a better way to get the parsed claims.
                // Possibly handle it in the OktaAuthService?
                const token = JSON.parse(atob(this.oktaAuthService.getAccessToken().split('.')[1]));

                envelope.baseData['properties'].pageName = pageName;

                envelope.baseData['properties'].hc_application_name = 'Health Catalyst Ignite™ Data and Analytics';
                envelope.baseData['properties'].hc_application_id = 4906;
                envelope.baseData['properties'].hc_publisher = 'front-end';
                envelope.baseData['properties'].hc_application_version = environment.versionNumber;
                envelope.baseData['properties'].hc_tenant_id = this.tenantContext.currentTenant?.tenantId || '';
                envelope.baseData['properties'].hc_environment = environment.environmentCode;
                envelope.baseData['properties'].hc_region_code = this.tenantContext.currentTenant?.locationId || '';
                envelope.baseData['properties'].hc_cloud = 'Azure';
                envelope.baseData['properties'].hc_identity_provider = 'okta';

                // TODO: remove this logic once uhash and uflag are available in DEV
                // see https://healthcatalyst.atlassian.net/browse/INT-117922
                if (
                    !!token &&
                    Object.prototype.hasOwnProperty.call(token, 'uflag') &&
                    Object.prototype.hasOwnProperty.call(token, 'uhash')
                ) {
                    if (token['uflag'] === '1') {
                        envelope.baseData['properties'].hc_user_id = token.preferred_username;
                        envelope.baseData['properties'].hc_user_type = 'health-catalyst';
                    } else {
                        envelope.baseData['properties'].hc_user_id = token['uhash'];
                        envelope.baseData['properties'].hc_user_type = 'client';
                    }
                } else {
                    envelope.baseData['properties'].hc_user_id = null;
                    envelope.baseData['properties'].hc_user_type = null;
                }

                if (this.debugLogToConsole) {
                    console.log('telemetryTraceContextInitializer:');
                    console.log('Trace ID ' + telemetryTraceContext.traceID);
                    console.log('Parent ID: ' + telemetryTraceContext.parentID);
                    console.log('Envelope name: ' + envelope.name);
                    console.log('Envelope base data: ');
                    console.log(envelope.baseData);
                }
            }
        };
        this.appInsights.addTelemetryInitializer(telemetryTraceContextInitializer);

        // This demonstrates an example of how to exclude certain UI controls from being auto-instrumented by the Click Plugin and sent to Insights:
        // Create a telemetry initializer to check if a control click captured by the Click plugin has a data attribute of 'data-do-not-send-to-insights'
        // If it does, return false, so the click event isn't logged
        // const doNotSendToInsightsIfDataAttributeExistsTelemetryInitializer = (envelope: ITelemetryItem) => {
        //     const telemetryTraceContext = this.appInsights.context?.telemetryTrace;

        //     if (telemetryTraceContext !== undefined) {
        //         // Check if its a click event - generated by the click plugin
        //         if (envelope.name === 'Microsoft.ApplicationInsights.{0}.Event') {
        //             envelope.data = envelope.data ?? {};
        //             // do a basic parse of the 'content' string to see if it contains the custom data attribute signifying we want to exclude it from App Insights
        //             if (envelope.data['content'].includes('do-not-send-to-insights')) {
        //                 // returning false stops the telemetry from being sent
        //                 return false;
        //             }
        //         }
        //     }

        //     return true;
        // }
        //this.appInsights.addTelemetryInitializer(doNotSendToInsightsIfDataAttributeExistsTelemetryInitializer);
    }

    /**
     * Create a new span
     * @param spanName Name of span
     */
    createNewSpan(spanName: string) {
        const telemetryTraceContext = this.appInsights.context;

        const traceId = telemetryTraceContext?.telemetryTrace.traceID ?? Util.generateW3CId();
        const newSpanId = this.newSpanId();
        const parentSpanId = telemetryTraceContext?.telemetryTrace.parentID ?? '0000000000000000';

        this.previousParentId = parentSpanId;
        this.appInsights.context.telemetryTrace.name = spanName;
        this.appInsights.context.telemetryTrace.traceID = traceId;
        this.appInsights.context.telemetryTrace.parentID = newSpanId;

        if (this.debugLogToConsole) {
            console.log('createNewSpan():');
            console.log('Trace name: ' + spanName);
            console.log('Trace ID: ' + traceId);
            console.log('Trace parent ID: ' + parentSpanId);
            console.log('New span ID: ' + newSpanId);
            console.log('telemetryTraceContext: ');
            console.log(telemetryTraceContext);
        }
    }

    // Generate random span id
    private newSpanId(): string {
        return Util.generateW3CId().substring(0, 16);
    }

    /**
     * Log a diagnostic scenario in app insights, with optional severityLevel and optional custom properties
     * @param message Message string to log
     * @param severityLevel Severity level, Information by default
     * @param properties Optional custom properties
     */
    public logMessage(message: string, severityLevel?: SeverityLevel, properties?: { [key: string]: any }) {
        this.appInsights.trackTrace({
            message: message,
            severityLevel: severityLevel || SeverityLevel.Information,
            properties: properties
        });
    }

    /**
     * Wrapper around app insights trackDependencyData()
     * Dependencies are automatically instrumented with the angular plugin instrumentation.
     * This method can be called in use cases where we need to do this explicitly.
     * @param response HttpResponse
     * @param request HttpRequest
     * @param startTime Start time
     */
    public logDependency(response: HttpResponse<any>, request: HttpRequest<any>, startTime: number) {
        // only track requests that have been returned.
        if (this.appInsightsConnectionString && response.type !== null) {
            const verb: string = request.method;
            const url: string = request.url;
            const elapsed: number = new Date().getTime() - startTime;
            const wasSuccessful: boolean = response.ok;
            const responseCode: number = response.status;

            this.appInsights.trackDependencyData({
                responseCode: responseCode,
                success: wasSuccessful,
                id: this.getUniqueId(),
                target: url,
                duration: elapsed,
                type: verb
            });
        }
    }

    /**
     * Wrapper around app insights trackPageView()
     * Page views are automatically instrumented with the angular plugin instrumentation.
     * This method can be called in use cases where we need to do this explicitly.
     * @param name Name
     */
    public logPageView(name: string) {
        if (this.appInsightsConnectionString) {
            const nameWithNoId: string = removePageViewNameIds(name);
            this.appInsights.trackPageView({
                name: nameWithNoId,
                uri: name
            });
        }
    }

    /**
     * Wrapper around app insights trackException()
     * @param exception Exception
     * @param severityLevel Optional severity level
     */
    public logException(exception: Error, severityLevel?: any) {
        if (this.appInsightsConnectionString) {
            this.appInsights.trackException({
                error: exception,
                exception: exception,
                id: this.getUniqueId(),
                severityLevel: severityLevel
            });
        }
    }

    public setAuthenticatedUserContext(userId: string, accountId?: string) {
        if (this.appInsightsConnectionString) {
            this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
        }
    }

    /**
     *  Certain events, like clicks, are automatically instrumented with the click plugin plugin instrumentation.
     *  This method can be called in use cases where we need to do this explicitly.
     * @param name Name
     * @param customProperties Optional custom properties
     */
    public logEvent(name: string, customProperties?: { [key: string]: any }) {
        this.appInsights.trackEvent({
            name: name,
            properties: customProperties
        });
    }

    public getTraceId(): string {
        return this.appInsights?.context?.telemetryTrace?.traceID ?? '';
    }

    private getUniqueId() {
        // https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
}
